import { useState } from "react";
import { Loader2, PackageCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { requestBackorder } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/context/I18nContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Lead-capture dialog for out-of-stock products. Submits to /api/backorder/request
 * which stores the lead and (when SMTP is configured) emails the shop.
 */
export const BackorderDialog = ({ open, onOpenChange, product, variant }) => {
  const { tx } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setQuantity(1); setMessage(""); setDone(false);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async () => {
    if (!EMAIL_RE.test(email)) { toast.error(tx("Please enter a valid email address")); return; }
    setSubmitting(true);
    try {
      await requestBackorder({
        handle: product?.handle,
        title: product?.title,
        variant_id: variant?.id || null,
        variant_title: variant?.title || null,
        quantity: Math.max(1, Math.min(999, Number(quantity) || 1)),
        name: name || null,
        email,
        phone: phone || null,
        message: message || null,
      });
      setDone(true);
      toast.success(tx("Backorder request received"), {
        description: tx("We'll be in touch as soon as this item is back in stock."),
      });
    } catch (e) {
      toast.error(tx("Could not submit request"), { description: tx("Please try again in a moment.") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="backorder-dialog">
        {done ? (
          <div className="flex flex-col items-center py-6 text-center" data-testid="backorder-success">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
              <PackageCheck className="h-7 w-7" />
            </span>
            <h3 className="mt-4 font-display text-xl">{tx("Request received")}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {tx("Thank you. We'll email you as soon as")} <span className="font-medium text-foreground">{product?.title}</span> {tx("is available to order again.")}
            </p>
            <Button className="mt-6 w-full" onClick={() => handleOpenChange(false)} data-testid="backorder-close-button">
              {tx("Done")}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{tx("Request Backorder")}</DialogTitle>
              <DialogDescription>
                {tx("This item is currently out of stock. Leave your details and we'll notify you the moment it's back.")}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-border bg-secondary/40 px-3.5 py-2.5 text-sm">
              <p className="font-medium leading-snug">{product?.title}</p>
              {variant?.title && variant.title !== "Default Title" && (
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">{variant.title}</p>
              )}
            </div>

            <div className="grid gap-3.5 py-1">
              <div className="grid gap-1.5">
                <Label htmlFor="bo-name" className="text-xs">{tx("Name")}</Label>
                <Input id="bo-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder={tx("Your name")} data-testid="backorder-name-input" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="bo-email" className="text-xs">{tx("Email")} *</Label>
                <Input id="bo-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required data-testid="backorder-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-3.5">
                <div className="grid gap-1.5">
                  <Label htmlFor="bo-phone" className="text-xs">{tx("Phone")}</Label>
                  <Input id="bo-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder={tx("Optional")} data-testid="backorder-phone-input" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="bo-qty" className="text-xs">{tx("Quantity")}</Label>
                  <Input id="bo-qty" type="number" min={1} max={999} value={quantity}
                    onChange={(e) => setQuantity(e.target.value)} data-testid="backorder-qty-input" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="bo-msg" className="text-xs">{tx("Message")}</Label>
                <Textarea id="bo-msg" value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder={tx("Any size, colour or timing notes?")} rows={3} data-testid="backorder-message-input" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}
                data-testid="backorder-cancel-button">
                {tx("Cancel")}
              </Button>
              <Button onClick={submit} disabled={submitting} data-testid="backorder-submit-button">
                {submitting
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {tx("Sending")}</>
                  : <><Send className="mr-2 h-4 w-4" /> {tx("Submit Request")}</>}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
